<template>
  <div class="update-resource-status">
    <!-- Current User's Name -->
    <h5 class="user-name">{{ userDisplayName }}</h5>
    <cv-form class="update-status" @submit.prevent>
      <!-- Remarks/feedback -->
      <dl class="field-set info">
        <dt class="field-label">{{ t("status_history_remark_title") }}</dt>
        <dd class="field-value">
          <text-area
            :mandatory="true"
            :character-limit="2000"
            :disabled="isInputDisabled"
            :do-validate="doValidateRemarks"
            :text-id="dirtyId"
            @change="handleRemarksChange"
            @validated="handleValidated"
          />
        </dd>
      </dl>
    </cv-form>

    <div class="action-toolbar">
      <!-- Cancel button -->
      <cv-button
        class="cancel"
        kind="tertiary"
        size="field"
        :disabled="actionButtonDisabled"
        @click="handleCancel"
      >
        {{ t("button_cancel") }}
      </cv-button>
      <!-- Save button -->
      <fetch-data-button
        class="save"
        v-bind="dataSaveButton"
        :disabled="actionButtonDisabled"
        @click="handleSaveNewStatus"
        @reset-completed-state="handleSaveButtonResetCompleted"
        @reset-error-state="handleSaveButtonResetError"
      >
        {{ saveButtonLabel }}
      </fetch-data-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { isEmpty } from "@utils/var.check";
import { useI18n } from "vue-i18n";
import { useDirty } from "@plugins/common/composables/page-dirty";

const emit = defineEmits(["submit", "cancel"]);
const { t } = useI18n();

// Props
const props = defineProps({
  resourceUuid: { type: String, required: true },
  resourceDomain: { type: String, required: true },
  userDisplayName: { type: String, default: "" },
  newStatus: { type: String, default: "" },
  lockService: { type: Object, default: () => ({}) },
  saveAction: { type: Function, default: () => ({}) },
  postSuccessStateAction: { type: Function, required: true },
});

// State
const remarks = ref("");
const doValidateRemarks = ref(false);

const dataSaveButton = ref({
  kind: "primary",
  size: "field",
  loading: false,
  error: false,
  completed: false,
  tipAlignment: "end",
  errorMessage: t("api_fetch_generic_error_message"),
});

const dirtyId = ref(`${props.resourceUuid}_WorkflowStatusUpdate`);
const { setDirty, removeDirty } = useDirty(dirtyId);

// Computed
const isInputDisabled = computed(
  () => dataSaveButton.value.loading || dataSaveButton.value.completed,
);

const actionButtonDisabled = computed(
  () => dataSaveButton.value.loading || dataSaveButton.value.completed,
);

const saveButtonLabel = computed(() =>
  props.resourceDomain === "moe-library" && props.newStatus === "REJECTED"
    ? t("button_reject")
    : t("button_return"),
);

// Methods
const handleRemarksChange = (newValue: string) => {
  setDirty(dirtyId.value);
  remarks.value = newValue;
};

const handleCancel = () => {
  emit("cancel");
};

const handleSaveNewStatus = () => {
  dataSaveButton.value.loading = true;
  doValidateRemarks.value = true;
};

const handleValidated = (isValid: boolean) => {
  doValidateRemarks.value = false;

  if (isValid) {
    saveForm();
  } else {
    dataSaveButton.value.loading = false;
  }
};

const saveForm = async () => {
  try {
    await props.lockService.checkLock();

    // clear dirty for cases when saveAction requires re-direction
    removeDirty(dirtyId.value);

    await props.saveAction(remarks.value);

    // generic success handler
    dataSaveButton.value.loading = false;
    dataSaveButton.value.completed = true;
  } catch (errMsg) {
    // re-apply dirty if there are failure in save form
    setDirty(dirtyId.value);

    // generic failure handler
    dataSaveButton.value.loading = false;
    dataSaveButton.value.error = true;
    if (!isEmpty(errMsg)) {
      dataSaveButton.value.errorMessage = errMsg as string;
    }
  }
};

const handleSaveButtonResetError = () => {
  dataSaveButton.value.error = false;
};

const handleSaveButtonResetCompleted = () => {
  props.postSuccessStateAction?.();
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.update-resource-status {
  padding-bottom: $spacing-05;
  border-bottom: 1px solid $ui-03;

  .action-toolbar {
    display: flex;
    justify-content: center;

    .bx--btn {
      + .button {
        margin-left: $spacing-05;
      }
    }
  }

  .user-name {
    @include carbon--type-style("productive-heading-01", true);
    margin-bottom: $spacing-02;
  }
}
</style>
